import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Paper,
    useTheme,
    useMediaQuery,
    IconButton,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import "./styles.css"
import { useDispatch } from "react-redux";
import { setAuthInfo } from "@/redux/auth/auth.slice";
import 'react-toastify/dist/ReactToastify.css';
import ToastService from "@/service/toaster.service";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { isVendorProfileComplete } from "@/helpers/vendor.helpers";
import vendorAuthService from "@/service/auth.service/vendor.auth.service";

const VendorLoginPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [emailError, setEmailError] = useState(""); // Email error state
    const [passwordError, setPasswordError] = useState(""); // Password error state
    const [showPassword, setShowPassword] = useState(false);  // State to control password visibility
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

    const navigate = useNavigate();

    // Validation function for email and password
    const validateForm = () => {
        let isValid = true;

        // Email validation
        if (!email) {
            setEmailError("Email is required.");
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            setEmailError("Please enter a valid email.");
            isValid = false;
        } else {
            setEmailError(""); // Clear error if valid
        }

        // Password validation
        if (!password) {
            setPasswordError("Password is required.");
            isValid = false;
        } else if (password.length < 6) {
            setPasswordError("Password must be at least 6 characters.");
            isValid = false;
        } else {
            setPasswordError(""); // Clear error if valid
        }

        return isValid;
    };

    // Handle login
    const handleLogin = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        try {
            // Step 1: Call Login API
            const response = await vendorAuthService.login(email.toLowerCase(), password);
            const {token:access_token, data:vendor} = response.data;
            vendor.id = vendor._id;
            dispatch(setAuthInfo({ ...vendor, access_token, role: "vendor" }));
            if(!isVendorProfileComplete(vendor)) {
                localStorage.setItem("access_token", access_token);
                localStorage.setItem("role", "vendor");                
                navigate("/vendor/onboarding/profile-wizard");
            }
            else{
                localStorage.setItem("access_token", access_token);
                localStorage.setItem("role", "vendor");           
                if (rememberMe) {
                    localStorage.setItem("email", email);
                    localStorage.setItem("password", password);
                } else {
                    localStorage.removeItem("email");
                    localStorage.removeItem("password");
                }
                // navigate("/vendor/dashboard");
            }
        } catch (error) {
            const errorData = error?.response?.data?.data;
            const message = error?.response?.data?.data?.error || error?.response?.data?.message || "An error occurred. Please try again.";
            if (errorData?.is_email_verified === false) {
                // Redirect to email verification step
                setTimeout(()=>{
                    navigate("/vendor/auth/verify-email-otp", { state: { email: email } });
                },1000)
            }
            else{
                ToastService.error(message);
            }

        } finally {
        }
    };

    // Handle input change and clear respective error
    const handleEmailChange = (e) => {
        let updatedEmail = e.target.value.trim().replace(/\s+/g, ' ');
        setEmail(updatedEmail);
        if (emailError) {
            setEmailError("");
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (passwordError) {
            setPasswordError(""); // Clear password error on input change
        }
    };

    // Load saved credentials if "Remember Me" is checked
    useEffect(() => {
        const storedEmail = localStorage.getItem("email");
        const storedPassword = localStorage.getItem("password");

        if (storedEmail && storedPassword) {
            setEmail(storedEmail);
            setPassword(storedPassword);
            setRememberMe(true); // Mark Remember Me as checked
        }
    }, []);


    return (

        <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
                    <Paper
                        sx={{
                        width: isMobile ? "100%" : isTablet ? "75%" : 450,
                        p: "102px 55px",
                        textAlign: "center",
                        borderRadius: 2,
                        border: "2px solid #CDCCF0",
                        }}
                        elevation={5}
                    >
                    <Typography variant="h4" color="#5E5ACF" fontWeight="bold">
                    Log-in
                    </Typography>
                <form onSubmit={handleLogin}>
                    {/* Email */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        variant="outlined"
                        value={email}
                        onChange={handleEmailChange}
                        error={Boolean(emailError)}
                        helperText={emailError} // Display error message below input
                    />

                    {/* Password */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Password"
                        type={showPassword ? "text" : "password"}  // Toggle between text and password
                        variant="outlined"
                        value={password}
                        onChange={handlePasswordChange}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            ),
                        }}
                        error={Boolean(passwordError)}
                        helperText={passwordError} // Display error message below input
                    />

                    {/* Remember me checkbox */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={rememberMe}
                                    onChange={() => setRememberMe(!rememberMe)}
                                />
                            }
                            label="Remember me"
                        />
                        <Typography
                            variant="body2"
                            color="primary"
                            sx={{ cursor: "pointer" }}
                        >
                            <Link to="/vendor/auth/forgot-password">Forgot password?</Link>
                        </Typography>
                    </Box>

                    {/* Display error message for invalid credentials */}
                    {error && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
                    )}

                    {/* Login Button */}
                    <Button
                        variant="contained"
                        fullWidth
                        className="btn-login"
                        sx={{
                            mt: 2,
                            color: "black",
                            fontWeight: 'bold',
                        }}
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? "Logging in..." : "Log in"}
                    </Button>
                </form>

                {/* Signup Link */}
                <Typography variant="body2" sx={{ mt: 2 }}>
                    Don't have an account?{" "}
                    <span className="text-primary">
                        <Link to="/vendor/auth/register">Sign up</Link>
                    </span>
                </Typography>
            </Paper>
        </Box>
    );
};

export default VendorLoginPage;
